import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface PublishDialogProps {
  open: boolean;
  /** Pre-generated default playable name */
  defaultName: string;
  /** Called when user clicks "发布" or presses Enter；clearHistory=发布后清空历史版本仅留基准 */
  onConfirm: (name: string, clearHistory: boolean) => void;
  /** Called when user clicks "取消", clicks overlay, or presses ESC */
  onCancel: () => void;
  /** Show loading state on publish button */
  loading?: boolean;
}

export function PublishDialog({
  open,
  defaultName,
  onConfirm,
  onCancel,
  loading = false,
}: PublishDialogProps): JSX.Element | null {
  const [name, setName] = useState(defaultName);
  const [clearHistory, setClearHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync defaultName when dialog opens
  useEffect(() => {
    if (open) { setName(defaultName); setClearHistory(false); }
  }, [open, defaultName]);

  // Auto-focus and select text when dialog opens
  useEffect(() => {
    if (open) {
      // Delay to ensure DOM is rendered
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.select();
        }
      });
    }
  }, [open]);

  // ESC key handler
  const handleKeyDown = useCallback(
    (e: DocumentEventMap['keydown']) => {
      if (e.key === 'Escape' && !loading) {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel, loading],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const handleConfirm = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed || loading) return;
    onConfirm(trimmed, clearHistory);
  }, [name, loading, clearHistory, onConfirm]);

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={loading ? undefined : onCancel}
    >
      <div
        className="glass-card w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h3 className="text-base font-semibold text-foreground">发布试玩</h3>

        {/* Label + Input */}
        <div className="mt-4">
          <label className="text-sm text-muted-foreground">试玩名称</label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleInputKeyDown}
            disabled={loading}
            className="input-neon mt-1.5 w-full px-3 py-2 text-sm"
            placeholder="请输入试玩名称"
          />
        </div>

        {/* 清空历史版本选项 */}
        <label className="mt-4 flex cursor-pointer items-start gap-2 select-none">
          <input
            type="checkbox"
            checked={clearHistory}
            onChange={(e) => setClearHistory(e.target.checked)}
            disabled={loading}
            className="mt-0.5 size-3.5 accent-cyan-400"
          />
          <span className="text-xs text-muted-foreground">
            发布后清空历史版本
            <span className="mt-0.5 block text-[10px] leading-relaxed text-muted-foreground/70">
              仅保留当前版本作为基准，删除其他历史版本与临时产物（不可恢复）
            </span>
          </span>
        </label>

        {/* Buttons */}
        <div className="mt-5 flex items-center justify-between gap-2">
          {/* Cancel — left */}
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            取消
          </button>

          {/* Publish — right */}
          <button
            onClick={handleConfirm}
            disabled={loading || !name.trim()}
            className={cn(
              'glow-btn flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? '发布中…' : '发布'}
          </button>
        </div>
      </div>
    </div>
  );
}
